<script >
import { use } from '@fegobull/webapi';
import dayjs from 'dayjs';
import { computed } from 'vue-demi';
import { getCurrencySymbol } from '../../utils/currency';
import { getCurrentPriceLabelByLang, getStockNameByLang, useFegoPageInfo } from '../common';
import { GuessResult, SessionStatus } from '../constants';
import locales from '../locales';
import { formatI18n } from '../utils/i18n';
const __sfc_main = {};
__sfc_main.props = {
  // 场次信息（包含 quote）
  session: {
    type: Object,
    required: true
  },
  // 当前用户的竞猜记录
  record: {
    type: Object,
    default: null
  },
  balance: {
    type: Number,
    default: 0
  },
  // 下一场未开始的场次（由父组件传入，用于截止态按钮展示）
  nextSession: {
    type: Object,
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    isCN,
    isHK,
    isEN,
    kls
  } = useFegoPageInfo();

  // 说明：避免散落的魔法数字，便于统一调整
  const TEN_W_THRESHOLD = 100000;
  const PERCENT_MAX = 100;
  const PRICE_DECIMALS = 2;
  const {
    useI18n
  } = use;
  const $t = useI18n(locales);
  const props = __props;
  const emit = __ctx.emit;
  const stockName = computed(() => {
    return getStockNameByLang(props.session.stockInfo, {
      isCN: isCN.value,
      isHK: isHK.value,
      isEN: isEN.value
    }, '-');
  });

  // 币种符号：避免模板里重复调用
  const currencySymbol = computed(() => getCurrencySymbol(props.session.stockInfo?.currencyCode));

  // 多语言文案片段
  const currentPriceLabel = computed(() => getCurrentPriceLabelByLang(stockName.value, {
    isCN: isCN.value,
    isHK: isHK.value,
    isEN: isEN.value
  }));

  // 展示用日期：优先使用 settle_time（YYYYMMDD）按语言渲染日期
  const settleDateLabel = computed(() => {
    const raw = props.session.settle_time;
    const s = raw !== null && raw !== undefined ? String(raw) : '';
    const settleDate = dayjs(s, 'YYYYMMDD', true);
    if (!settleDate.isValid()) {
      return '';
    }
    if (isEN.value) {
      // eslint-disable-next-line rulesdir/dayjs-date-format -- 说明：按需求不使用 @futuweb/tool-date-formatter，保留 dayjs 格式化
      return settleDate.format('MMM D');
    }
    // eslint-disable-next-line rulesdir/dayjs-date-format -- 说明：按需求不使用 @futuweb/tool-date-formatter，保留 dayjs 格式化
    return settleDate.format('M月D日');
  });

  // 标题模板：使用方案 B（模板插值），避免 prefix/suffix 拼接
  const closePriceTitleParts = computed(() => {
    // 仅替换 date，保留 {stock} 用于在模板中高亮展示
    const tpl = String($t('closePriceTitleTpl'));
    const s = formatI18n(tpl, {
      date: settleDateLabel.value
    });
    const parts = s.split('{stock}');
    return {
      beforeStock: parts[0],
      afterStock: parts.slice(1).join('{stock}')
    };
  });

  // 是否已结算（结果已出）
  const isSettled = computed(() => Number(props.session.status) === SessionStatus.Settled);

  // 是否猜中：后端结算脚本会把 record.result 置为 1-胜
  const isGuessedWin = computed(() => Number(props.record?.result) === GuessResult.Win);
  const showProphetTitle = computed(() => isSettled.value && isGuessedWin.value);

  // 猜中标题模板拆分（{stock} 用于高亮）
  const prophetTitleParts = computed(() => {
    const tpl = String($t('prophetCongratsTpl'));
    const parts = tpl.split('{stock}');
    return {
      beforeStock: parts[0],
      afterStock: parts.slice(1).join('{stock}')
    };
  });

  // 猜中且已结算时的“打败 xx% 的人”提示
  // 说明：当前按产品文案展示固定值，后续如需接入真实击败比例可改为服务端下发
  const beatPercentText = computed(() => '99.99%');
  const showBeatTip = computed(() => showProphetTitle.value);
  const beatTipParts = computed(() => {
    // 保留 {percent} 用于把数字部分单独高亮
    const tpl = String($t('beatPeopleTpl'));
    const parts = tpl.split('{percent}');
    return {
      before: parts[0],
      after: parts.slice(1).join('{percent}')
    };
  });

  // 猜中且已结算时，前置展示“xx 人参与，”
  const participateCountPrefixText = computed(() => {
    const n = Number(props.session.participateCount || 0);
    if (!Number.isFinite(n) || n <= 0) {
      return '';
    }
    const countStr = n >= TEN_W_THRESHOLD ? '10w+' : String(n);
    const tpl = String($t('participateCountPrefixTpl'));
    return formatI18n(tpl, {
      count: countStr
    });
  });

  // 是否已截止：优先使用后端 status，兜底用 end_time 判断
  // 约定（与后端表一致）：2-已截止 3-已结算 4-已取消
  const isGuessEnded = computed(() => {
    const s = Number(props.session.status);
    if ([SessionStatus.Ended, SessionStatus.Settled, SessionStatus.Cancelled].includes(s)) {
      return true;
    }
    const endTime = props.session.end_time;
    if (!endTime) {
      return false;
    }
    const end = dayjs(endTime);
    if (!end.isValid()) {
      return false;
    }
    return dayjs().isAfter(end);
  });

  // 下一场开始时间展示（仅当下一场“未开始”时返回）
  const nextRoundStartLabel = computed(() => {
    const ns = props.nextSession;
    if (!ns) {
      return '';
    }
    const startTime = ns?.show_start_time;
    if (!startTime) {
      return '';
    }
    const start = dayjs(startTime);
    if (!start.isValid()) {
      return '';
    }
    if (!dayjs().isBefore(start)) {
      return '';
    }
    if (isEN.value) {
      // eslint-disable-next-line rulesdir/dayjs-date-format -- 说明：按需求不使用 @futuweb/tool-date-formatter，保留 dayjs 格式化
      return start.format('MMM D HH:mm');
    }
    // eslint-disable-next-line rulesdir/dayjs-date-format -- 说明：按需求不使用 @futuweb/tool-date-formatter，保留 dayjs 格式化
    return start.format('M/DD HH:mm');
  });
  const endedButtonText = computed(() => {
    if (!isGuessEnded.value) {
      return '';
    }
    // 说明：最后一轮竞猜且未结算时，按钮展示“待开奖”
    const isLastRound = !props.nextSession;
    const status = Number(props.session.status);
    if (isLastRound && !isSettled.value && status !== SessionStatus.Cancelled) {
      return String($t('pendingDraw'));
    }
    const nextStart = nextRoundStartLabel.value;
    if (nextStart) {
      const prefix = String($t('nextRound'));
      return isEN.value ? `${prefix} ${nextStart}` : `${prefix}${nextStart}`;
    }
    return String($t('ended'));
  });

  // 是否已关注该标的（由后端在 listGuesses 登录态返回）
  const isWatched = computed(() => Boolean(props.session.isWatched));

  // 是否已填写预测值
  const hasGuessPrice = computed(() => {
    const v = props.record?.guess_price;
    return v !== null && v !== undefined && String(v).trim() !== '';
  });

  // 预测值展示：最多保留 2 位小数（并去掉尾随 0）
  const displayGuessPrice = computed(() => {
    if (!hasGuessPrice.value) {
      return '--';
    }
    const raw = props.record?.guess_price;
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      return '--';
    }
    // 说明：固定保留两位小数，尾随 0 也需要展示（例如 120.00）
    return n.toFixed(PRICE_DECIMALS);
  });

  // 参与统计展示：“已有23456人參與，85%的牛友認爲會上漲”
  // 规则：
  // 1) 人数 > 10w 显示 10w+
  // 2) >=50% 认为会上涨（猜收盘价：guess_price >= 现价；猜涨跌：guess_updown=Up）
  // 3) 否则认为会下跌
  // 说明：为了给“上涨/下跌”增加颜色，这里拆分为 head + tail 两段
  const statInfo = computed(() => {
    const n = Number(props.session.participateCount || 0);
    const pct = Number(props.session.trendPercent ?? NaN);
    const dir = String(props.session.trendDirection ?? '');

    // console.log('statInfo', n, pct, dir);
    if (!Number.isFinite(n) || n <= 0) {
      return null;
    }
    if (!Number.isFinite(pct)) {
      return null;
    }
    if (dir !== 'up' && dir !== 'down') {
      return null;
    }
    const countStr = n >= TEN_W_THRESHOLD ? '10w+' : String(n);
    const trendWord = dir === 'up' ? String($t('statUpWord')) : String($t('statDownWord'));
    const pctStr = String(Math.max(0, Math.min(PERCENT_MAX, Math.round(pct))));
    // 说明：使用模板插值，保留 {trend} 以便单独高亮
    const tpl = String($t('statTextTpl'));
    const s = formatI18n(tpl, {
      count: countStr,
      pct: pctStr,
      trend: '{trend}'
    });
    const parts = s.split('{trend}');
    return {
      head: parts[0],
      tailSuffix: parts.slice(1).join('{trend}'),
      trendWord,
      dir
    };
  });

  // 最新提交的 3 人（头像由统计接口返回）
  const latestSubmitters = computed(() => Array.isArray(props.session.latestSubmitters) ? props.session.latestSubmitters : []);

  // 提交竞猜
  const handleSubmit = () => {
    if (isGuessEnded.value) {
      return;
    }
    // 仅负责向上抛出“打开表单”的事件，由父组件决定如何展示 stock-form
    emit('click', {
      sessionId: props.session.session_id,
      session: props.session,
      record: props.record
    });
  };

  // 点击自选：仅抛事件，由父组件决定登录/接口/状态更新逻辑
  const handleWatchClick = () => {
    const stockId = String(props.session.stock_id || '');
    const sessionId = String(props.session.session_id || '');
    if (!stockId || !sessionId) {
      return;
    }
    emit('watch-click', {
      sessionId,
      stockId,
      isWatched: isWatched.value
    });
  };
  return {
    kls,
    $t,
    props,
    stockName,
    currencySymbol,
    currentPriceLabel,
    closePriceTitleParts,
    showProphetTitle,
    prophetTitleParts,
    beatPercentText,
    showBeatTip,
    beatTipParts,
    participateCountPrefixText,
    isGuessEnded,
    endedButtonText,
    isWatched,
    hasGuessPrice,
    displayGuessPrice,
    statInfo,
    latestSubmitters,
    handleSubmit,
    handleWatchClick
  };
};
export default __sfc_main;
</script>

<template>
    <div class="close-price-guess" :class="kls">
        <div class="title">
            <template v-if="showProphetTitle">
                <span>{{ prophetTitleParts.beforeStock }}</span>
                <span class="em">{{ stockName }}</span>
                <span>{{ prophetTitleParts.afterStock }}</span>
            </template>
            <template v-else-if="isGuessEnded">{{ $t('roundEnded') }}</template>
            <template v-else>
                {{ closePriceTitleParts.beforeStock
                }}<!--
                --><span class="em">{{ stockName }}</span
                ><!--
                -->{{ closePriceTitleParts.afterStock }}
            </template>
        </div>

        <div class="price-container">
            <div class="price-item current-price">
                <span class="price-item-label">{{ currentPriceLabel }}</span>
                <span class="price-item-value-container">
                    <span class="price-item-value">{{
                        props.session.quote ? props.session.quote.nominalPrice : '--'
                    }}</span>
                    <span class="price-item-dollar">{{ currencySymbol }}</span>
                </span>
                <i class="icon-watch" :class="{ watched: isWatched }" @click.stop="handleWatchClick"></i>
            </div>
            <div :class="['price-item', 'user-price', { empty: !hasGuessPrice }]">
                <span class="price-item-label">{{ $t('yourPrediction') }}</span>
                <span class="price-item-value-container">
                    <span class="price-item-value">{{ displayGuessPrice }}</span>
                    <span v-if="hasGuessPrice" class="price-item-dollar">{{ currencySymbol }}</span>
                </span>
            </div>
        </div>

        <!-- 参与统计展示 / 猜中提示 -->
        <div v-if="showBeatTip" class="stat-text">
            <span v-if="latestSubmitters.length" class="latest-avatars">
                <img
                    v-for="x in latestSubmitters"
                    :key="String(x.cid || x.avatar || '')"
                    class="avatar"
                    :src="x.avatar"
                    alt=""
                />
            </span>
            <span v-if="participateCountPrefixText">{{ participateCountPrefixText }}</span>
            <span>{{ beatTipParts.before }}</span>
            <span class="stat-highlight">{{ beatPercentText }}</span>
            <span>{{ beatTipParts.after }}</span>
        </div>
        <div v-else-if="statInfo" class="stat-text">
            <span v-if="latestSubmitters.length" class="latest-avatars">
                <img
                    v-for="x in latestSubmitters"
                    :key="String(x.cid || x.avatar || '')"
                    class="avatar"
                    :src="x.avatar"
                    alt=""
                />
            </span>
            <span>{{ statInfo.head }}</span>
            <span class="stat-trend" :class="statInfo.dir === 'up' ? 'is-up' : 'is-down'">{{
                statInfo.trendWord
            }}</span>
            <span>{{ statInfo.tailSuffix }}</span>
            <i class="stat-trend-icon" :class="statInfo.dir === 'up' ? 'is-up' : 'is-down'"></i>
        </div>

        <button
            class="submit-btn"
            :class="{ 'modify-btn': record, disabled: isGuessEnded }"
            :disabled="isGuessEnded"
            @click="handleSubmit"
        >
            <template v-if="isGuessEnded">
                {{ endedButtonText }}
            </template>
            <template v-else>
                {{ $t(record ? 'modify' : 'predict') }}
                <span v-if="record" class="balance-text">{{ balance }}</span>
            </template>
        </button>
    </div>
</template>

<style lang="less" scoped>
.close-price-guess {
    width: 6.7rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 auto;
    padding: 0.5rem 0.35rem 0 0.35rem;

    .title {
        font-family: FZLanTingHeiS-DB1-GBK;
        font-size: 0.36rem;
        line-height: 0.42rem;
        color: #262626;
        font-weight: 400;
    }

    .stat-text {
        height: 0.52rem;
        border-radius: 0.52rem;
        margin-top: 0.3rem;
        font-size: 0.2rem;
        // line-height: .52rem;
        color: #626262;
        font-family: FZLanTingHei-M-BIG5;
        text-align: center;
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.6) 0%,
            rgba(255, 255, 255, 0.36) 49.72%,
            rgba(255, 255, 255, 0.06) 100%
        );
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        .stat-highlight {
            // 说明：高亮击败比例数字
            color: #ff6103;
            // font-weight: 700;
            font-family: BR Hendrix;
        }
        .stat-trend {
            // 说明：上涨/下跌需要额外颜色强调
            &.is-up {
                color: #00a870;
            }
            &.is-down {
                color: #f03a55;
            }
        }
        .stat-trend-icon {
            display: inline-block;
            width: 0.15rem;
            height: 0.18rem;
            margin-left: 0.02rem;
            vertical-align: -0.02rem;
            background-repeat: no-repeat;
            background-size: 100% 100%;
            &.is-up {
                background-image: url('../images/icon-up.png');
            }
            &.is-down {
                background-image: url('../images/icon-down.png');
            }
        }
        .latest-avatars {
            display: inline-flex;
            align-items: center;
            margin-left: 0.12rem;
            margin-right: 0.03rem;
            .avatar {
                width: 0.3rem;
                height: 0.3rem;
                border-radius: 50%;
                object-fit: cover;
                border: 0.02rem solid #fff;
                margin-left: -0.08rem;
                background: #f2f2f2;
            }
            .avatar:first-child {
                margin-left: 0;
            }
        }
    }

    .em {
        color: #ff6900;
    }

    .price-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        margin-top: 0.3rem;
        .price-item {
            display: flex;
            flex-direction: column;
            position: relative;
            width: 2.9rem;
            height: 1.6rem;
            border-radius: 0.16rem;
            padding-top: 0.22rem;
            padding-left: 0.22rem;
        }
        .current-price {
            background: rgba(255, 255, 255, 0.5);
            color: #262626;
        }
        .user-price {
            background: linear-gradient(289deg, #ff6c01 12.03%, #ff8d0d 102.81%);
            color: #fff;
            &.empty {
                background: rgba(255, 255, 255, 0.5);
                color: #262626;
            }
        }

        .price-item-label {
            font-size: 0.24rem;
            line-height: 0.32rem;
            font-family: FZLanTingHeiS-DB1-GBK;
        }
        .price-item-value {
            margin-top: 0.11rem;
            font-size: 0.44rem;
            font-weight: 700;
            font-family: 'BR Hendrix';
            line-height: 1;
        }
        .price-item-dollar {
            font-size: 0.28rem;
            font-family: 'BR Hendrix';
            line-height: 1;
            font-weight: 600;
        }

        // 自选 icon（未关注：icon-like；已关注：icon-liked）
        .icon-watch {
            position: absolute;
            right: 0.3rem;
            top: 0.27rem;
            width: 0.29rem;
            height: 0.25rem;
            background-repeat: no-repeat;
            background-size: 100% 100%;
            background-image: url('../images/icon-like.png');
            cursor: pointer;
            &.watched {
                width: 0.27rem;
                height: 0.23rem;
                background-image: url('../images/icon-liked.png');
            }
        }
    }

    .price-item-value-container {
        display: flex;
        flex-direction: column;
    }

    .submit-btn {
        margin-top: 0.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-family: FZLanTingHeiS-DB1-GBK;
        font-size: 0.36rem;
        color: #fff;
        width: 5.4rem;
        height: 1rem;
        border-radius: 1rem;
        border: 0.02rem solid #fff;
        background: linear-gradient(93deg, #ff930f -6.59%, #ff6900 88.73%);
        position: relative;
        &.modify-btn {
            background: linear-gradient(97deg, #575757 -5.74%, #282828 25.86%, #282828 86.62%, #575757 105.61%),
                linear-gradient(93deg, #ff930f -6.59%, #ff6900 88.73%);
        }
        &.disabled,
        &:disabled {
            background: linear-gradient(0deg, #989898 0%, #989898 100%),
                linear-gradient(97deg, #575757 -5.74%, #282828 25.86%, #282828 86.62%, #575757 105.61%),
                linear-gradient(93deg, #ff930f -6.59%, #ff6900 88.73%);
            cursor: not-allowed;
            opacity: 1;
        }

        .balance-text {
            position: absolute;
            right: 0.49rem;
            top: -0.14rem;
            font-size: 0.2rem;
            color: #fff;
            background: #ff6900;
            width: 0.46rem;
            height: 0.28rem;
            border-top-left-radius: 0.12rem;
            border-top-right-radius: 0.28rem;
            border-bottom-right-radius: 0.28rem;
        }
    }
}

.close-price-guess.is-pc {
    // background-color: #ccc;
    width: 11.8rem !important;
    padding-left: 0.34rem;
    padding-right: 0.4rem;

    .price-container {
        .price-item {
            width: 5.43rem;
            height: 1.51rem;
            padding-left: 0.3rem;
            padding-top: 0.3rem;
        }
        .price-item-value {
            margin-top: 0.15rem;
            font-size: 0.42rem;
            line-height: 1;
        }
        .price-item-dollar {
            font-size: 0.24rem;
            margin-top: 0.24rem;
            margin-left: 0.08rem;
        }
    }

    .price-item-value-container {
        display: flex;
        align-items: center;
        flex-direction: row;
    }

    .icon-watch {
        top: 0.3rem;
    }

    .stat-text {
        height: 0.36rem;
        // 说明：PC 端不铺满容器宽度，按内容自适应并居中展示
        width: fit-content;
        padding-left: 15px;
        padding-right: 30px;
        margin-left: auto;
        margin-right: auto;
        box-sizing: border-box;
    }

    .submit-btn {
        margin-top: 0.2rem;
        width: 3.6rem;
        height: 0.6rem;
        border-radius: 0.6rem;
        font-size: 0.2rem;

        .balance-text {
            right: 0.22rem;
        }
    }
}
</style>
